import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTonAddress } from '@tonconnect/ui-react';
import { shortenAddress } from '@cherry/shared';
import { api, type TradeActivity } from '../lib/api';
import { LotCard } from '../components/LotCard';
import { LotCardSkeletonList } from '../components/Skeleton';
import { EmptyState } from '../components/EmptyState';
import { events } from '../lib/analytics';
import { usePullToRefresh } from '../hooks/usePullToRefresh';
import { EscrowExplainer } from '../components/EscrowExplainer';
import { MarketDataPanel } from '../components/MarketDataPanel';
import { EventBanner } from '../components/EventBanner';
import { Tooltip } from '../components/Tooltip';
import { useSavedSearches } from '../hooks/useSavedSearches';
import { useToast } from '../components/Toast';

function HowItWorks({ onDismiss }: { onDismiss: () => void }) {
  const [showEscrow, setShowEscrow] = useState(false);

  return (
    <div className="border border-cherry-300/40 bg-cherry-50/30 dark:bg-cherry-900/10 rounded-xl p-4 mb-6 relative">
      <button
        onClick={onDismiss}
        className="absolute top-2 right-2 min-w-[44px] min-h-[44px] flex items-center justify-center text-text-muted hover:text-text-primary text-lg leading-none rounded-lg"
        aria-label="Dismiss"
      >&times;</button>
      <h2 className="text-base font-bold mb-3">Welcome to Cherry Trading Post</h2>
      <p className="text-sm text-text-secondary mb-3">
        The P2P trading floor for TON. Bundle any mix of assets, negotiate deals directly with other traders, and swap <Tooltip content="No middleman required — the smart contract enforces the trade.">trustlessly</Tooltip> — no middlemen, no liquidity pools, no slippage.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
        <div className="flex items-start gap-3">
          <svg className="w-7 h-7 shrink-0 text-cherry-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v6m3-3H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <p className="font-semibold">Post your deal</p>
            <p className="text-text-secondary text-xs">Bundle any mix of TON, <Tooltip content="A token on TON (like a memecoin or stablecoin).">jettons</Tooltip>, and NFTs. Describe what you want in return.</p>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <svg className="w-7 h-7 shrink-0 text-cherry-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
          </svg>
          <div>
            <p className="font-semibold">Negotiate P2P</p>
            <p className="text-text-secondary text-xs">Traders make counter-offers with their own asset bundles. Pick the best deal.</p>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <svg className="w-7 h-7 shrink-0 text-cherry-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
          </svg>
          <div>
            <p className="font-semibold">Swap in one step</p>
            <p className="text-text-secondary text-xs">Accept and everything exchanges in one transaction. <Tooltip content="Held securely by a smart contract until the deal completes.">Escrow</Tooltip> protection, zero counterparty risk.</p>
          </div>
        </div>
      </div>
      <div className="mt-3">
        <button
          onClick={() => setShowEscrow((v) => !v)}
          className="text-xs text-ton-500 hover:text-ton-600 font-medium transition-colors"
        >
          {showEscrow ? 'Hide details' : 'Learn how escrow works →'}
        </button>
        {showEscrow && (
          <div className="mt-2">
            <EscrowExplainer />
          </div>
        )}
      </div>
    </div>
  );
}

function getRelativeTime(date: Date, now: number): string {
  const diff = now - date.getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function RecentTradesTicker({ trades, now }: { trades: TradeActivity[]; now: number }) {
  if (trades.length === 0) return null;

  const latest = trades[0];
  const assetSummary = (assets: TradeActivity['sellerAssets']) => {
    if (assets.length === 0) return 'assets';
    const kinds = assets.map((a) => a.kind);
    const unique = [...new Set(kinds)];
    if (unique.length === 1 && unique[0] === 'TON') return 'TON';
    return `${assets.length} asset${assets.length !== 1 ? 's' : ''}`;
  };

  const ago = getRelativeTime(new Date(latest.filledAt), now);

  return (
    <Link
      to="/activity"
      className="flex items-center gap-2 px-3 py-2 mb-4 rounded-lg bg-success-bg border border-success-border text-xs no-underline transition-colors hover:bg-success-bg/80"
    >
      <span className="inline-block w-2 h-2 rounded-full bg-success animate-pulse shrink-0" />
      <span className="text-success-fg font-medium truncate">
        {trades.length} trade{trades.length !== 1 ? 's' : ''} completed
      </span>
      <span className="text-text-muted">·</span>
      <span className="text-text-secondary truncate">
        {shortenAddress(latest.seller)} swapped {assetSummary(latest.sellerAssets)} for {assetSummary(latest.buyerAssets)} {ago}
      </span>
      <span className="ml-auto text-ton-500 shrink-0">View all &rarr;</span>
    </Link>
  );
}

const SORT_OPTIONS = [
  { value: 'createdAt:desc', label: 'Newest first' },
  { value: 'createdAt:asc', label: 'Oldest first' },
  { value: 'expiresAt:asc', label: 'Expiring soon' },
  { value: 'offerCount:desc', label: 'Most offers' },
  { value: 'buyNow:asc', label: 'Buy Now: low to high' },
] as const;

const ONBOARDING_DISMISSED_KEY = 'cherry_onboarding_dismissed';

export function BrowsePage() {
  const address = useTonAddress();
  const { toast } = useToast();
  const { create: saveSearch, isCreating: isSaving } = useSavedSearches();
  const [searchQuery, setSearchQuery] = useState('');
  const [assetFilter, setAssetFilter] = useState('');
  const [collectionFilter, setCollectionFilter] = useState('');
  const [sortKey, setSortKey] = useState('createdAt:desc');
  // nosemgrep: semgrep.cherry-storage-usestate-init -- SPA-only (no SSR/hydration today); revisit at Mathom fold-in (CM-500); audited 2026-08-18
  const [showOnboarding, setShowOnboarding] = useState(
    () => localStorage.getItem(ONBOARDING_DISMISSED_KEY) !== '1',
  );

  const dismissOnboarding = useCallback(() => {
    setShowOnboarding(false);
    localStorage.setItem(ONBOARDING_DISMISSED_KEY, '1');
  }, []);

  const [sort, order] = sortKey.split(':');

  const [modelFilter, setModelFilter] = useState('');

  const showCollectionFilter = assetFilter === 'TG_GIFT' || assetFilter === 'NFT';
  const hasActiveFilters = !!searchQuery || !!assetFilter || !!collectionFilter || !!modelFilter;

  const { data: giftCollections } = useQuery({
    queryKey: ['gift-collections'],
    queryFn: () => api.getGiftCollections(),
    staleTime: 5 * 60_000,
    enabled: showCollectionFilter,
  });

  const { data: collectionAttrs } = useQuery({
    queryKey: ['gift-collection-attrs', collectionFilter],
    queryFn: () => api.getGiftCollectionAttributes(collectionFilter),
    staleTime: 5 * 60_000,
    enabled: !!collectionFilter,
  });

  const handleSaveSearch = useCallback(async () => {
    if (!address) return;
    const parts: string[] = [];
    if (searchQuery) parts.push(`"${searchQuery}"`);
    if (assetFilter) parts.push(assetFilter);
    if (collectionFilter) parts.push(collectionFilter);
    if (modelFilter) parts.push(modelFilter);
    const name = parts.length > 0 ? parts.join(' + ') : 'All deals';
    try {
      await saveSearch({
        name,
        queryParams: {
          query: searchQuery || undefined,
          assetKind: assetFilter || undefined,
          collection: collectionFilter || undefined,
        },
        notifyEnabled: true,
      });
      toast('Search saved to watchlist', 'success');
    } catch {
      toast('Failed to save search', 'error');
    }
  }, [address, searchQuery, assetFilter, collectionFilter, modelFilter, saveSearch, toast]);

  const { data: recentTrades, dataUpdatedAt: activityUpdatedAt } = useQuery({
    queryKey: ['activity-summary'],
    queryFn: () => api.getActivity(5),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const { data: lots, isLoading, error, refetch } = useQuery({
    queryKey: ['lots', searchQuery, assetFilter, collectionFilter, modelFilter, sort, order],
    queryFn: () => api.getLots({
      query: searchQuery || undefined,
      assetKind: assetFilter || undefined,
      collectionName: collectionFilter || undefined,
      giftModel: modelFilter || undefined,
      sort,
      order,
    }),
    refetchInterval: 30_000,
  });

  const { containerRef, PullIndicator } = usePullToRefresh(() => refetch());

  const prevSearchRef = useRef('');
  useEffect(() => {
    if (searchQuery && searchQuery !== prevSearchRef.current && lots !== undefined) {
      events.searchPerformed({ query: searchQuery, resultCount: lots.length });
    }
    prevSearchRef.current = searchQuery;
  }, [searchQuery, lots]);

  return (
    <div ref={containerRef} className="mx-auto max-w-3xl px-5 py-6">
      {PullIndicator}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">Browse Deals</h1>
        <div className="flex items-center gap-3">
          <Link
            to="/activity"
            className="text-xs text-text-muted hover:text-text-secondary font-medium no-underline transition-colors"
          >
            Activity &rarr;
          </Link>
          <Link
            to="/features"
            className="text-xs text-ton-500 hover:text-ton-600 font-medium no-underline transition-colors"
          >
            Vote on features &rarr;
          </Link>
        </div>
      </div>

      {showOnboarding && <HowItWorks onDismiss={dismissOnboarding} />}

      {recentTrades && recentTrades.length > 0 && (
        <RecentTradesTicker trades={recentTrades} now={activityUpdatedAt} />
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <input
          type="text"
          placeholder="Search deals..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="flex-1 px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ton-500/60 focus:border-border-focus transition-shadow"
        />
        <select
          value={assetFilter}
          onChange={(e) => { setAssetFilter(e.target.value); setCollectionFilter(''); setModelFilter(''); }}
          className="px-3 py-2 border border-border rounded-lg text-sm bg-surface focus:outline-none focus:ring-2 focus:ring-ton-500/60 focus:border-border-focus"
        >
          <option value="">All assets</option>
          <option value="TG_GIFT">🎁 Telegram Gifts</option>
          <option value="TON">TON</option>
          <option value="JETTON">Jetton</option>
          <option value="NFT">NFT</option>
          <option value="TG_NUMBER">TG Number</option>
          <option value="TG_USERNAME">TG Username</option>
          <option value="DNS">DNS</option>
        </select>
      </div>

      {showCollectionFilter && giftCollections && giftCollections.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {giftCollections.map((c) => (
            <button
              key={c.collectionName}
              onClick={() => {
                const next = collectionFilter === c.collectionName ? '' : c.collectionName;
                setCollectionFilter(next);
                setModelFilter('');
              }}
              className={`px-2.5 py-1 text-xs font-medium rounded-full border transition-colors ${
                collectionFilter === c.collectionName
                  ? 'bg-cherry-500 text-white border-cherry-500'
                  : 'bg-surface-alt text-text-secondary border-border hover:border-cherry-300'
              }`}
            >
              {c.collectionName} ({c.count})
            </button>
          ))}
        </div>
      )}

      {collectionAttrs && collectionAttrs.models.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-4">
          <span className="text-xs text-text-muted self-center mr-1">Model:</span>
          {collectionAttrs.models.map((m) => (
            <button
              key={m}
              onClick={() => setModelFilter(modelFilter === m ? '' : m)}
              className={`px-2 py-0.5 text-xs rounded-full border transition-colors ${
                modelFilter === m
                  ? 'bg-purple-500 text-white border-purple-500'
                  : 'bg-surface-alt text-text-secondary border-border hover:border-purple-300'
              }`}
            >
              {m}
            </button>
          ))}
        </div>
      )}

      {/* Sort + Save Search */}
      <div className="flex items-center gap-2 mb-6">
        <span className="text-xs text-text-muted">Sort by</span>
        <select
          value={sortKey}
          onChange={(e) => setSortKey(e.target.value)}
          className="px-2 py-1 border border-border rounded-lg text-xs bg-surface focus:outline-none focus:ring-2 focus:ring-ton-500/60"
        >
          {SORT_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        {address && hasActiveFilters && (
          <button
            onClick={handleSaveSearch}
            disabled={isSaving}
            className="ml-1 px-2.5 py-1 text-xs font-medium text-cherry-600 hover:text-cherry-700 border border-cherry-300/50 hover:border-cherry-400 bg-cherry-50/30 rounded-lg transition-colors disabled:opacity-50"
          >
            {isSaving ? 'Saving...' : 'Save search'}
          </button>
        )}
        {lots && (
          <span className="ml-auto text-xs text-text-muted">
            {lots.length} deal{lots.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      <EventBanner />

      <MarketDataPanel />

      {isLoading && <LotCardSkeletonList count={4} />}

      {error && (
        <div className="text-center py-8" role="alert">
          <p className="text-text-secondary mb-3">Couldn't reach the server. Check your connection and tap Retry.</p>
          <button onClick={() => refetch()} className="px-4 py-2 bg-surface-alt border border-border rounded-lg text-sm font-medium hover:bg-surface transition-colors">
            Try Again
          </button>
        </div>
      )}

      {lots && lots.length === 0 && (
        <EmptyState
          variant="search"
          title="No deals found"
          description="Be the first to post a deal!"
        />
      )}

      {/* Lot grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" aria-live="polite">
        {lots?.map((lot) => (
          <LotCard key={lot.lotId} lot={lot} />
        ))}
      </div>
    </div>
  );
}
